/**
 * Material → cloud library save pipeline.
 *
 * Mirrors saveToLibrary.ts for materials: capture a preview sphere,
 * serialize the SceneMaterial JSON, upload both to R2 via presigned
 * URLs, and create a libraryItems row via the shared API.
 *
 * Materials are tiny (< 1 KB JSON) so every export is a full
 * round-trip — no delta encoding, no partial uploads. Dedup-by-hash
 * short-circuits the upload when the same material content already
 * lives in the caller's library.
 */

import type { LibraryItemResponse } from "@poseref/shared";
import { uploadToPresignedUrl } from "../api/client.ts";
import {
  checkQuota,
  findItemByHash,
  getPresignedUploadUrl,
} from "../api/upload.ts";
import type { SceneMaterial } from "../modeling/types.ts";
import { renderMaterialThumbnailBlob } from "../ui/materialGallery/materialThumbnailRenderer.ts";
import { useAuthStore } from "./authStore.ts";
import { computeFileHash } from "./fileHash.ts";
import { useLibraryStore } from "./libraryStore.ts";
import { setCachedLibraryMaterial } from "./materialLibraryCache.ts";

// ---------------------------------------------------------------------------
// Canonical JSON serialization
// ---------------------------------------------------------------------------

/**
 * Serialize a SceneMaterial to deterministic JSON so structurally
 * identical materials hash to the same sourceHash. Keys are emitted
 * in a fixed order — we rely on the SceneMaterial surface being a
 * plain object with no nested collections.
 */
export function serializeMaterial(properties: SceneMaterial): string {
  const source = properties as unknown as Record<string, unknown>;
  const keys = Object.keys(source).sort();
  const canonical: Record<string, unknown> = {};
  for (const k of keys) {
    const v = source[k];
    if (v !== undefined) canonical[k] = v;
  }
  return JSON.stringify(canonical);
}

async function hashJson(json: string): Promise<string> {
  const buf = new TextEncoder().encode(json).buffer as ArrayBuffer;
  return computeFileHash(buf);
}

// ---------------------------------------------------------------------------
// Export a scene material → new library item
// ---------------------------------------------------------------------------

/**
 * Upload a SceneMaterial as a new library item. Returns the created
 * LibraryItemResponse, or null on failure. Dedup is handled by the
 * backend sourceHash check — if a byte-identical material already
 * exists for this user, the existing item is reused (no new row).
 */
export async function exportMaterialToCloudLibrary(
  name: string,
  properties: SceneMaterial,
): Promise<LibraryItemResponse | null> {
  try {
    const json = serializeMaterial(properties);
    const fileBlob = new Blob([json], { type: "application/json" });
    const sourceHash = await hashJson(json);

    // Dedup: if an identical material already exists in the user's library,
    // reuse it instead of uploading again.
    const existing = await findItemByHash(sourceHash).catch(() => null);
    if (existing?.exists && existing.itemId) {
      const items = useLibraryStore.getState().items;
      const found = items.find((i) => i.id === existing.itemId);
      if (found && found.type === "material") {
        setCachedLibraryMaterial(found.id, properties);
        return found;
      }
    }

    await checkQuota(fileBlob.size);

    const thumbnailBlob = await renderMaterialThumbnailBlob(properties).catch(
      () => null,
    );

    const timestamp = Date.now();
    const rand = Math.random().toString(36).slice(2, 8);
    const fileKey = `library/materials/${timestamp}_${rand}.json`;
    const thumbnailKey = thumbnailBlob
      ? `library/thumbnails/${timestamp}_${rand}.png`
      : undefined;

    const filePresign = await getPresignedUploadUrl(
      fileKey,
      "application/json",
    );
    await uploadToPresignedUrl(filePresign.url, fileBlob);

    if (thumbnailBlob && thumbnailKey) {
      const thumbPresign = await getPresignedUploadUrl(
        thumbnailKey,
        "image/png",
      );
      await uploadToPresignedUrl(thumbPresign.url, thumbnailBlob);
    }

    const item = await useLibraryStore.getState().createItem({
      name,
      type: "material",
      fileKey,
      thumbnailKey,
      sourceHash,
      fileSize: fileBlob.size,
    });

    setCachedLibraryMaterial(item.id, properties);
    useAuthStore.getState().refreshStorageUsage();
    return item;
  } catch (e) {
    console.error("Failed to export material to library:", e);
    return null;
  }
}
